import React, { useEffect, useState, useCallback, useRef } from "react";
import ContactAPI from "../../api/ContactAPI";
import {
    FaFax,
    FaFacebookF,
    FaYoutube,
    FaLine,
    FaTiktok,
    FaFacebookMessenger
} from "react-icons/fa";
import { FiPhone } from "react-icons/fi";
import { IoLocationOutline } from "react-icons/io5";
import { HiOutlineMail } from "react-icons/hi";
import { getContactOfficeData } from "../../api/home";
import { useTranslation } from "react-i18next";

const SocialIcon = ({ type, link }) => {
    const icons = {
        facebook: <FaFacebookF />,
        messager: <FaFacebookMessenger />,
        youtube: <FaYoutube />,
        line: <FaLine />,
        tiktok: <FaTiktok />
    };
    const colors = {
        facebook: "bg-[#1b77f2]",
        messager: "bg-[#0084ff]",
        youtube: "bg-[#ff0200]",
        line: "bg-[#06c755]",
        tiktok: "bg-[#010101]"
    };
    return (
        <a href={link} className={`square-social ${colors[type]} text-lg`}>
            {icons[type]}
        </a>
    );
};

const ContactItem = ({ title, value, refProp, highlight }) => {
    const icons = {
        "ที่ตั้ง": <IoLocationOutline className="w-6 h-6 md:w-9 md:h-9 text-white" />,
        "โทรศัพท์": <FiPhone className="w-5 h-5 md:w-7 md:h-7 text-white" />,
        "โทรสาร": <FaFax className="w-5 h-5 md:w-6 md:h-6 text-white" />,
        "อีเมล": <HiOutlineMail className="w-5 h-5 md:w-8 md:h-8 text-white" />
    };
    const icon = icons[title] || null;
    return (
        <div
            ref={refProp}
            style={{
                backgroundColor: highlight ? "#caf0f8" : "transparent",
                transition: "background-color 0.5s ease"
            }}
            className="flex items-center space-x-3 mb-4"
        >
            {icon && (
                <div className={`flex items-center justify-center ${title.includes("ที่ตั้ง") ? "w-40 h-10 md:w-24 md:h-16" : title.includes("อีเมล") ? "w-24 h-10 md:w-16 md:h-16" : "w-10 h-10 md:w-16 md:h-16"} rounded-md bg-[#E9EAF8]`}>
                    <div className="flex items-center justify-center w-6 h-6 md:w-12 md:h-12 rounded-full bg-blue-900">
                        {icon}
                    </div>
                </div>
            )}
            <div className="flex flex-col">
                <p className="font-bold text-[#3D3D3D] text-[16px]">{title}</p>
                <p className="text-[#3D3D3D] text-[16px]">{value}</p>
            </div>
        </div>
    );
};

function Contact() {
    const { t, i18n } = useTranslation();
    const [details, setDetails] = useState([]);
    const [footerData, setFooterData] = useState("");
    const [highlightLocation, setHighlightLocation] = useState(false);
    const [highlightOfficer, setHighlightOfficer] = useState(false);

    const locationRef = useRef(null);
    const officerRef = useRef(null);

    const getData = useCallback(async () => {
        try {
            const response = await getContactOfficeData();

            setFooterData(response);
        } catch (error) {
            console.warn(error);
        }
    }, []);
    
    const fetchData = async () => {
        try {
            const response = await (i18n.language === 'en' ? ContactAPI.getDetailContactEn() : ContactAPI.getDetailContact());
            setDetails(response.data);
        } catch (error) {
            console.log("ContactAPI.getDetailContact", error);
        }
    };

    useEffect(() => {
        fetchData();
        getData();
    }, [getData, i18n.language]);

    useEffect(() => {
        const path = window.location.pathname;

        if (path === "/contact/location" && locationRef.current) {
            locationRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            setHighlightLocation(true);
            setTimeout(() => setHighlightLocation(false), 3000);
        }

        if (path === "/contact/contact_officer" && officerRef.current) {
            officerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            setHighlightOfficer(true);
            setTimeout(() => setHighlightOfficer(false), 3000);
        }
    }, [details]);

    const isSimilarToOfficerTitle = (text) =>
        text.includes("เจ้าหน้าที่") && text.includes("ข้อมูลส่วนบุคคล");

    return (
        <>
            <section className="banner bg-news-intro">
                <img
                    className="banner-image w-full"
                    src={"/images/contracts_bg/banner_contact.jpeg"}
                    alt="Hero Banner"
                />
                <div className="headline text-center">
                    <h1 className="headline-title text-3xl md:text-6xl font-bold text-white">
                        {details.title}
                    </h1>
                </div>
            </section>
            <section className="content p-4 md:p-8 bg-gray-50">
                <div className="content-container box-contact-container">
                    <div className="content-header w-full">
                        <div className="grid grid-cols-1 md:grid-cols-3">
                            <div className="col-span-2 bg-white p-6 rounded-l-lg">
                                <h1 className="text-2xl font-bold mb-6 text-black">{details.title}</h1>
                                <div className="mb-6">
                                    {details.contactgroup &&
                                        details.contactgroup.map((group, index) => (
                                            <div
                                                key={index}
                                                ref={isSimilarToOfficerTitle(group.grouptitle) ? officerRef : null}
                                                style={{
                                                    backgroundColor: isSimilarToOfficerTitle(group.grouptitle) && highlightOfficer
                                                        ? "#caf0f8"
                                                        : "transparent",
                                                    transition: "background-color 0.5s ease"
                                                }}
                                                className={`mb-6 ${group.grouptitle !== "ติดตามเราได้ที่" ? "border-b border-slate-700" : ""}`}
                                            >
                                                <h2 className="font-bold text-xl mb-4 text-blue-900">
                                                    {group.grouptitle}
                                                </h2>
                                                <div>
                                                    {group.groupitem.map((item, idx) => (
                                                        !["social:facebook", "social:messager", "social:youtube", "social:line", "social:tiktok"].includes(item.itemtitle) && (
                                                            <ContactItem key={idx} title={item.itemtitle} value={item.itemvalue} refProp={item.itemtitle === "ที่ตั้ง" ? locationRef : null}
                                                                highlight={item.itemtitle === "ที่ตั้ง" && highlightLocation} />
                                                        )
                                                    ))}
                                                </div>
                                                <div className="flex space-x-3 mb-4">
                                                    {group.groupitem.map((item, idx) => (
                                                        <div key={idx}>
                                                            {["social:facebook", "social:messager", "social:youtube", "social:line", "social:tiktok"].includes(item.itemtitle) && (
                                                                <SocialIcon type={item.itemtitle.split(":")[1]} link={item.itemvalue} />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                            <div className="col-span-1 bg-gray-100 p-6 rounded-r-lg shadow-md">
                                <h2 className="font-bold text-black text-2xl mb-6">
                                    {details.righttitle}
                                </h2>
                                {details.pgpgroup &&
                                    details.pgpgroup.length > 0 &&
                                    details.pgpgroup.map((group, index) => (
                                        <div key={index} className="text-xl mb-6 text-blue-900 border-b border-slate-700">
                                            <p className="font-bold text-xl mb-4" dangerouslySetInnerHTML={{ __html: group.grouptitle }}></p>
                                            {group.groupitem.map((item, idx) => (
                                                <div key={idx} className="mb-4">
                                                    <p dangerouslySetInnerHTML={{ __html: item.itemtitle }} className="font-bold text-[#3D3D3D] text-[16px]"></p>
                                                    <p dangerouslySetInnerHTML={{ __html: item.itemvalue }} className="text-[#3D3D3D] text-[16px]"></p>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                {details.gpgdownload && (
                                    <div>
                                        <h2 className="font-bold text-black text-xl mb-4">
                                            {details.gpgdownload.itemtitle}
                                        </h2>
                                        <div className="flex items-center">
                                            <img src="/images/icons/pdf.svg" alt="PDF Icon" />
                                            <a href={details.gpgdownload.itemlink} className="text-blue-600 cursor-pointer" target="blank">
                                                {details.gpgdownload.itemvalue}
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section>
                <div dangerouslySetInnerHTML={{ __html: footerData?.mapiframe }} />
            </section>
        </>
    );
}

export default Contact;
